import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EditProductCategorySheet } from '../-components/EditProductCategorySheet';
import { ProductCategoriesTable } from '../-components/ProductCategoriesTable';
import { adminProductCategoriesQueryOptions } from '../-hooks/useAdminProductCategories';
import { useProductCategoriesPage } from '../-hooks/useProductCategoriesPage';

const categoriesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
  /**
   * Which level of the tree is on screen. Absent is the root list; an id is
   * that category's product types.
   *
   * In the URL rather than component state, so a drilled-in list survives a
   * refresh and can be linked to — the old dashboard's version of this was
   * lost on both.
   */
  parentId: z.string().min(1).optional(),
});

export const Route = createFileRoute('/management/product-categories/(pages)/')(
  {
    validateSearch: categoriesSearch,
    loaderDeps: ({ search }) => ({
      page: search.page ?? 1,
      limit: search.limit ?? 50,
      parentId: search.parentId ?? null,
      ...(search.sort !== undefined ? { sort: search.sort } : {}),
      ...(search.filters !== undefined ? { filters: search.filters } : {}),
    }),
    // Swallowed on purpose: the loader is a prefetch, and letting it reject
    // hands the route to the router's error boundary, which hides the table's
    // own "Could not load" banner. Matches the other management lists.
    loader: ({ deps }) =>
      queryClient
        .ensureQueryData(adminProductCategoriesQueryOptions(deps))
        .catch(() => undefined),
    component: ProductCategoriesPage,
  },
);

function ProductCategoriesPage() {
  const { parentId } = Route.useSearch();
  const page = useProductCategoriesPage(parentId ?? null);

  return (
    <PageContainer>
      <ProductCategoriesTable page={page} />
      <EditProductCategorySheet page={page} />
    </PageContainer>
  );
}
